<template>
  <section class="wt-location">
    <div class="wt-location-inner">
      <welcome-title-advanced :title-text="locationTitle" />
      <div class="wt-location-labels">
        <span
          v-for="(label, i) in locationLabels"
          :key="i"
          class="wt-location-label"
        >{{
          label.name
        }}</span>
      </div>
      <vue-glide
        ref="slider"
        :type="'carousel'"
        :start-at="2"
        :bound="true"
        :focus-at="'center'"
        :per-view="5"
        :gap="25"
        :breakpoints="{
          992: {
            perView: 1,
          },
          1280: {
            perView: 3,
          },
        }"
        class="wt-location-carousel"
      >
        <vue-glide-slide
          v-for="(slide, i) in locationSlides"
          :key="i"
        >
          <img
            v-if="slide.image"
            class="wt-location-carousel-image"
            :src="slide.image"
            alt=""
          >
          <img
            v-if="!slide.image"
            class="wt-location-carousel-image"
            src="@/assets/images/placeholder.png"
            alt=""
          >
          <p class="wt-location-carousel-quote">
            {{ slide.quote }}
          </p>
          <p class="wt-location-carousel-author">
            {{ slide.author }}
          </p>
        </vue-glide-slide>
        <template slot="control">
          <div class="overlay overlay--left" />
          <div class="overlay overlay--right" />
          <button
            data-glide-dir="<"
            class="glide__arrow glide__arrow--left"
            :class="{ 'glide__arrow--disabled': noPrevSlide }"
          >
            <arrow-left class="icon" />
          </button>
          <button
            data-glide-dir=">"
            class="glide__arrow glide__arrow--right"
            :class="{ 'glide__arrow--disabled': noNextSlide }"
          >
            <arrow-right class="icon" />
          </button>
        </template>
      </vue-glide>
    </div>
    <div class="svg-container">
      <curve-alternative
        class="curve-alternative"
      />
    </div>
  </section>
</template>

<script>
import { Glide, GlideSlide } from 'vue-glide-js';
import 'vue-glide-js/dist/vue-glide.css';

export default {
  name: 'WelcomeLocation',
  components: {
    [Glide.name]: Glide,
    [GlideSlide.name]: GlideSlide,
    ArrowLeft: () => import(/* webpackChunkName: "main"*/ '@/components/svg/ArrowLeft.vue'),
    ArrowRight: () => import(/* webpackChunkName: "main"*/ '@/components/svg/ArrowRight.vue'),
    CurveAlternative: () => import(/* webpackChunkName: "main"*/ '@/components/svg/CurveAlternative.vue'),
    WelcomeTitleAdvanced: () =>
      import(/* webpackChunkName: "main" */ '@/components/welcome/WelcomeTitleAdvanced.vue'),
  },
  props: {
    locationTitle: {
      type: String,
      default: '',
    },
    locationLabels: {
      type: Array,
      default: null,
    },
    locationSlides: {
      type: Array,
      default: null,
    },
  },
  data() {
    return {
      noPrevSlide: false,
      noNextSlide: false,
    };
  },
  methods: {
    carouselArrowStates() {
      if (this.$refs.slider.glide.index === 0) {
        this.noPrevSlide = true;
      } else {
        this.noPrevSlide = false;
      }

      if (this.$refs.slider.glide.index + 1 === this.locationSlides.length) {
        this.noNextSlide = true;
      } else {
        this.noNextSlide = false;
      }
    },
  },
};
</script>

<style lang="postcss">
.wt-location {
  @apply w-full bg-primary-delta pt-14 pb-16 relative;

  &-inner {
    @apply container mx-auto relative;
    z-index: 1;
  }

  .wt-title-advanced {
    @apply mb-8;
  }

  &-labels {
    @apply flex justify-center mb-14 flex-wrap gap-3;

    .wt-location-label {
      @apply inline-flex justify-center items-center py-2 px-5 rounded-lg bg-transparent text-lg text-black border border-black;

      &:last-of-type {
        @apply m-0;
      }
    }
  }

  &-carousel {
    .wt-location-carousel {
      position: relative;

      &-image {
        @apply mb-9;
        height: 270px;
      }

      &-quote {
        quotes: '“' '”' '‘' '’';
        @apply text-xl font-semibold text-center opacity-0 h-0 mb-0;
        transition: opacity 200ms ease-in-out;

        &::before {
          content: open-quote;
          font-family: initial;
        }
        &::after {
          content: close-quote;
          font-family: initial;
        }
      }

      &-author {
        @apply text-base text-center;
      }
    }

    .glide__bullets {
      position: absolute;
      z-index: 2;
      bottom: 2em;
      left: 50%;
      display: inline-flex;
      list-style: none;
      transform: translateX(-50%);
    }

    .glide__bullet {
      background-color: var(--color-primary);
      width: 9px;
      height: 9px;
      padding: 0;
      border-radius: 50%;
      border: 2px solid transparent;
      transition: all 300ms ease-in-out;
      cursor: pointer;
      line-height: 0;
      box-shadow: 0 0.25em 0.5em 0 rgba(0, 0, 0, 0.1);
      margin: 0 0.25em;

      &:focus {
        outline: none;
      }

      &:hover,
      &:focus {
        border: 2px solid white;
        background-color: rgba(255, 255, 255, 0.5);
      }

      &--active {
        background-color: #b81c22;
      }
    }

    .glide__slide {
      position: relative;

      img {
        width: 100%;
        max-width: 320px;
        height: 320px;
        object-fit: cover;
        mask-image: url('~@/assets/svg/shield-cutout.svg?inline');
        mask-size: cover;
        margin: 0 auto;
        margin-bottom: 44px;

        @screen lg {
          width: 320px;
        }

        @screen xl {
          width: 236px;
          height: 236px;
        }
      }

      &--active {
        .wt-location-carousel {
          &-quote {
            @apply h-auto opacity-100 mb-4;
          }
        }
      }
    }

    .overlay {
      @apply absolute top-0 left-0 bg-gradient-to-l from-transparent to-primary-delta;
      width: 250px;
      height: 320px;
      display: none;

      &--right {
        @apply left-auto right-0 from-primary-delta to-transparent;
      }

      @screen md {
        display: block;
      }
    }

    .glide__arrow {
      @apply block absolute text-primary-bravo p-2 rounded-xs opacity-100 cursor-pointer;
      z-index: 2;
      box-shadow: 0 0.25em 0.5em 0 rgba(0, 0, 0, 0.1);
      will-change: opacity;
      transition: opacity 150ms ease, border 300ms ease-in-out;
      top: 115px;
      background-color: rgba(255, 255, 255, 0.6);

      .icon {
        width: 18px;
        height: 18px;
      }

      &:focus {
        outline: none;
      }

      &--left {
        left: 5px;
      }

      &--right {
        right: 5px;
      }

      &--disabled {
        opacity: 0.33;
        pointer-events: none;
      }
    }
  }

  .svg-container {
    position: absolute;
    bottom: -1px;
    left: 0;
    width: 100%;
    height: auto;
    display: block;
    padding-bottom: 9.72222vw;
    overflow: hidden;
  }

  .curve-alternative {
    position: absolute;
    top: 0;
    height: 100%;
    width: 100vw;
    left: 0;
    fill: #ffffff;
    color: #ffffff;
  }
}
</style>
